import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Box, Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import AssignmentIcon from "@material-ui/icons/Assignment";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import FlagIcon from "@material-ui/icons/Flag";
import { BUTTON_ICON_MAP } from "./buttonIconLibrary";
import { CANVAS_WIDTH, CANVAS_HEIGHT, VIEWPORTS, REGISTRATION_PAGE_ID, normalizePageTransitionId, ER_ANIMATION_KEYFRAMES_CSS } from "./escapeRoomEditorConstants";
import { ElementRenderer } from "./escapeRoomElementRenderer";
import { getElementEntryAnimationStyle } from "./escapeRoomElementEntryWrap";
import {
	useAfterSecondsButtonReady,
	useHideAfterSecondsExpired,
	isButtonHiddenByShowWhen,
	isButtonHidden,
} from "./escapeRoomButtonVisibility";
import EscapeRoomPageGlitchOverlay, { getPageTransitionWrapperStyle } from "./escapeRoomPageGlitchOverlay";
import EscapeRoomPageMistOverlay from "./escapeRoomPageMistOverlay";
import EscapeRoomPageBlueprintOverlay from "./escapeRoomPageBlueprintOverlay";
import {
	buildComicStaggerIndexMap,
	getComicPageBackgroundStyle,
	shouldApplyComicPageElementTransition,
} from "./escapeRoomComicPageTransition";
import {
	buildScatterStaggerIndexMap,
	getScatterPageBackgroundStyle,
	shouldApplyScatterPageElementTransition,
} from "./escapeRoomScatterPageTransition";
import {
	buildRevealStaggerIndexMap,
	getRevealPageBackgroundStyle,
	shouldApplyRevealPageElementTransition,
} from "./escapeRoomRevealPageTransition";
import {
	isStaggerEffectTransition,
	buildStaggerEffectIndexMap,
	getStaggerEffectPageBackgroundStyle,
	shouldApplyStaggerEffectPageElementTransition,
} from "./escapeRoomStaggerEffectTransitions";
import { resolvePlayQuizId } from "./escapeRoomPageNormalize";
import {
	normScId,
	computeExclusiveBranchGroupsFromPlay,
	scenariosVisitedUpToPlayPage,
} from "./escapeRoomPlayGraph";

function PlayModeView({
	playModeContainerRef,
	pages,
	playPageIndex,
	playViewport,
	playViewportDim,
	playScale,
	setPlayScale,
	setPlayMode,
	setPlayViewport,
	playTransitionKey,
	playTransitionType,
	playTransitionDuration,
	setPlayTransitionType,
	setPlayTransitionDuration,
	setPlayTransitionKey,
	setPlayPageIndex,
	playPage,
	playElements,
	isPlayRegPage,
	bgImgResolved,
	handlePlayButtonAction,
	handlePlayPrev,
	handlePlayNext,
	handleExitPlay,
	handleQuizOptionSelect,
	handleQuizCustomAnswer,
	handleQuizHintUsed,
	setPlayHintModal,
	playHintModal,
	playQuizAnswers,
	playTotalPoints,
	playModalContent,
	setPlayModalContent,
	playQuizFeedbackModal,
	setPlayQuizFeedbackModal,
	quizzes,
	scenarios = [],
	playActivatedByPage = {},
	playFooterOpen,
	setPlayFooterOpen,
	playSuppressedScenarioIds,
	playShowResults,
	onCloseResults,
}) {
	const quizPointsTotal = Object.values(playQuizAnswers || {}).reduce((s, a) => s + (a?.totalPoints ?? 0), 0);
	const totalPointsDisplay = quizPointsTotal + (playTotalPoints || 0);
	const updatePlayScale = useCallback(() => {
		const container = playModeContainerRef.current;
		if (!container) return;
		const w = playViewportDim?.width || CANVAS_WIDTH;
		const h = playViewportDim?.height || CANVAS_HEIGHT;
		if (!w || !h) return;
		const rect = container.getBoundingClientRect();
		const navBarH = 72;
		const pad = 32;
		const availW = Math.max(100, rect.width - pad);
		const availH = Math.max(100, rect.height - navBarH - pad);
		const scale = Math.min(availW / w, availH / h, 3);
		setPlayScale(Math.max(0.5, scale));
	}, [playModeContainerRef, playViewportDim?.width, playViewportDim?.height, setPlayScale]);

	const updatePlayScaleRef = useRef(updatePlayScale);
	updatePlayScaleRef.current = updatePlayScale;

	useEffect(() => {
		const el = playModeContainerRef.current;
		if (!el) return;
		const enterFullscreen = () => {
			try {
				const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
				if (req) {
					const p = req.call(el);
					if (p && typeof p.catch === "function") p.catch(() => {});
				}
			} catch (err) {}
		};
		enterFullscreen();
		const onFullscreenChange = () => {
			if (!document.fullscreenElement && !document.webkitFullscreenElement) setPlayMode(false);
			else setTimeout(() => { if (updatePlayScaleRef.current) updatePlayScaleRef.current(); }, 100);
		};
		document.addEventListener("fullscreenchange", onFullscreenChange);
		document.addEventListener("webkitfullscreenchange", onFullscreenChange);
		setTimeout(() => { if (updatePlayScaleRef.current) updatePlayScaleRef.current(); }, 150);
		return () => {
			document.removeEventListener("fullscreenchange", onFullscreenChange);
			document.removeEventListener("webkitfullscreenchange", onFullscreenChange);
			const inFullscreen = document.fullscreenElement || document.webkitFullscreenElement;
			if (inFullscreen) {
				try {
					if (document.exitFullscreen) document.exitFullscreen();
					else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
				} catch (err) {}
			}
		};
	}, [playModeContainerRef, setPlayMode]);

	useEffect(() => {
		const raf = requestAnimationFrame(() => updatePlayScale());
		let ro;
		if (playModeContainerRef.current) {
			ro = new ResizeObserver(() => requestAnimationFrame(updatePlayScale));
			ro.observe(playModeContainerRef.current);
		}
		return () => {
			cancelAnimationFrame(raf);
			if (ro) ro.disconnect();
		};
	}, [playModeContainerRef, playPageIndex, playViewport, updatePlayScale]);

	const playAnimType = normalizePageTransitionId(playTransitionType || "none");
	const isComicPlayTransition = playAnimType === "comic";
	const isScatterPlayTransition = playAnimType === "scatter";
	const isRevealPlayTransition = playAnimType === "reveal";
	const isStaggerEffectPlayTransition = isStaggerEffectTransition(playAnimType);
	const pageStagger = isComicPlayTransition
		? buildComicStaggerIndexMap(playElements || [])
		: isScatterPlayTransition
			? buildScatterStaggerIndexMap(playElements || [])
			: isRevealPlayTransition
				? buildRevealStaggerIndexMap(playElements || [])
				: isStaggerEffectPlayTransition
					? buildStaggerEffectIndexMap(playElements || [])
					: null;
	const staggerBgStyle = isComicPlayTransition
		? getComicPageBackgroundStyle(playTransitionDuration || 550)
		: isScatterPlayTransition
			? getScatterPageBackgroundStyle(playTransitionDuration || 550)
			: isRevealPlayTransition
				? getRevealPageBackgroundStyle(playTransitionDuration || 550)
				: isStaggerEffectPlayTransition
					? getStaggerEffectPageBackgroundStyle(playAnimType, playTransitionDuration || 550)
					: null;
	const afterSecondsReady = useAfterSecondsButtonReady(playPageIndex, playElements);
	const playButtonShowCtxBase = useMemo(() => {
		const quizIds = (playElements || []).filter((e) => e.type === "quiz").map((e) => e.elementId);
		const allQuizzesOnPageCompleted = quizIds.length === 0 || quizIds.every((id) => {
			const a = playQuizAnswers?.[id];
			return !!(a && a.done);
		});
		const activatedSet = playActivatedByPage[String(playPageIndex)] || new Set();
		const pageButtons = (playElements || []).filter((e) => e.type === "button");
		return { allQuizzesOnPageCompleted, pageButtons, activatedSet, afterSecondsReady };
	}, [playElements, playQuizAnswers, playActivatedByPage, playPageIndex, afterSecondsReady]);
	const baseVisibleButtonIds = useMemo(() => {
		const s = new Set();
		for (const el of playButtonShowCtxBase.pageButtons) {
			if (!isButtonHiddenByShowWhen(el, playButtonShowCtxBase)) s.add(String(el.elementId));
		}
		return s;
	}, [playButtonShowCtxBase]);
	const hideAfterExpired = useHideAfterSecondsExpired(playPageIndex, playElements, baseVisibleButtonIds);
	const playPageAnimKey = `${playTransitionKey}-${playPageIndex}`;
	const modalWasOpenRef = useRef(false);
	const [modalEntryAnimKey, setModalEntryAnimKey] = useState(0);
	useEffect(() => {
		if (playModalContent) {
			if (!modalWasOpenRef.current) {
				modalWasOpenRef.current = true;
				setModalEntryAnimKey((k) => k + 1);
			}
		} else {
			modalWasOpenRef.current = false;
		}
	}, [playModalContent]);
	const modalElements = playModalContent?.design?.elements || [];
	const modalAfterSecondsReady = useAfterSecondsButtonReady(
		playModalContent ? modalEntryAnimKey : -1,
		modalElements
	);
	const modalButtonShowCtxBase = useMemo(() => {
		const quizIds = (modalElements || []).filter((e) => e.type === "quiz").map((e) => e.elementId);
		const allQuizzesOnPageCompleted = quizIds.length === 0 || quizIds.every((id) => {
			const a = playQuizAnswers?.[id];
			return !!(a && a.done);
		});
		const activatedSet = playActivatedByPage[String(playPageIndex)] || new Set();
		const pageButtons = (modalElements || []).filter((e) => e.type === "button");
		return { allQuizzesOnPageCompleted, pageButtons, activatedSet, afterSecondsReady: modalAfterSecondsReady };
	}, [modalElements, playQuizAnswers, playActivatedByPage, playPageIndex, modalAfterSecondsReady]);
	const modalBaseVisibleButtonIds = useMemo(() => {
		const s = new Set();
		for (const el of modalButtonShowCtxBase.pageButtons) {
			if (!isButtonHiddenByShowWhen(el, modalButtonShowCtxBase)) s.add(String(el.elementId));
		}
		return s;
	}, [modalButtonShowCtxBase]);
	const modalHideAfterExpired = useHideAfterSecondsExpired(
		playModalContent ? modalEntryAnimKey : -1,
		modalElements,
		modalBaseVisibleButtonIds
	);

	if (playShowResults) {
		return (
			<Box
				ref={playModeContainerRef}
				style={{
					position: "fixed",
					inset: 0,
					background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
					zIndex: 1300,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Box style={{ width: 420, maxWidth: "90vw", background: "#fff", borderRadius: 18, padding: "28px 24px", boxShadow: "0 18px 60px rgba(0,0,0,0.35)", textAlign: "center" }}>
					<Box fontSize={28} fontWeight={800} color="#0f172a" mb={1}>Resultados</Box>
					<Box fontSize={13} color="#64748b" mb={2}>Fin del escape room</Box>
					<Box style={{ padding: "14px 16px", borderRadius: 12, background: "#f8fafc", border: "1px solid #e2e8f0", marginBottom: 16 }}>
						<Box fontSize={12} color="#64748b" mb={0.5}>Puntuación total</Box>
						<Box fontSize={34} fontWeight={800} color="#f59e0b">{totalPointsDisplay}</Box>
					</Box>
					<Button variant="contained" onClick={onCloseResults} style={{ textTransform: "none", background: "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)", color: "#fff" }}>
						Cerrar resultados
					</Button>
				</Box>
			</Box>
		);
	}

	return (
		<Box
			ref={playModeContainerRef}
			position="fixed"
			top={0}
			left={0}
			right={0}
			bottom={0}
			zIndex={9999}
			display="flex"
			flexDirection="column"
			bgcolor="#0f172a"
		>
			<style>{`
				@keyframes er-modal-overlay-in { from { opacity: 0; } to { opacity: 1; } }
				@keyframes er-modal-content-in { from { opacity: 0; transform: scale(0.94); } to { opacity: 1; transform: scale(1); } }
			`}</style>
			<Box position="absolute" top={12} left={12} right={12} zIndex={10} display="flex" alignItems="center" justifyContent="space-between">
				<Box display="flex" alignItems="center" style={{ gap: 6 }}>
					{VIEWPORTS.map((vp) => {
						const Icon = vp.Icon;
						const active = playViewport === vp.id;
						return (
							<Tooltip key={vp.id} title={vp.label}>
								<IconButton
									onClick={() => setPlayViewport(vp.id)}
									style={{
										background: active ? "rgba(124,58,237,0.9)" : "rgba(255,255,255,0.9)",
										color: active ? "#fff" : "#334155",
									}}
									size="small"
								>
									<Icon style={{ fontSize: 20 }} />
								</IconButton>
							</Tooltip>
						);
					})}
					<Box fontSize={12} color="#94a3b8" ml={1}>
						{VIEWPORTS.find((v) => v.id === playViewport)?.label}
					</Box>
				</Box>
				<IconButton onClick={handleExitPlay} style={{ background: "rgba(255,255,255,0.9)", color: "#334155" }} title="Salir (Esc)">
					<CloseIcon />
				</IconButton>
			</Box>
			<style>{`
				${ER_ANIMATION_KEYFRAMES_CSS}
				@keyframes er-play-fade { from { opacity: 0; } to { opacity: 1; } }
				@keyframes er-play-slideLeft { from { opacity: 0; transform: translateX(20%); } to { opacity: 1; transform: translateX(0); } }
				@keyframes er-play-slideRight { from { opacity: 0; transform: translateX(-20%); } to { opacity: 1; transform: translateX(0); } }
				@keyframes er-play-slideUp { from { opacity: 0; transform: translateY(20%); } to { opacity: 1; transform: translateY(0); } }
				@keyframes er-play-slideDown { from { opacity: 0; transform: translateY(-20%); } to { opacity: 1; transform: translateY(0); } }
				@keyframes er-play-zoom { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
				@keyframes er-feedback-fadeIn { from { opacity: 0; } to { opacity: 1; } }
				@keyframes er-feedback-popCorrect { from { opacity: 0; transform: scale(0.5); } to { opacity: 1; transform: scale(1); } }
				@keyframes er-feedback-popWrong { from { opacity: 0; transform: scale(0.5) rotate(-3deg); } to { opacity: 1; transform: scale(1) rotate(0deg); } }
				@keyframes er-feedback-iconBounce { 0% { transform: scale(0); opacity: 0; } 50% { transform: scale(1.15); } 70% { transform: scale(0.95); } 100% { transform: scale(1); opacity: 1; } }
			`}</style>
			<Box flex={1} display="flex" alignItems="center" justifyContent="center" p={2} overflow="hidden">
				<Box
					key={playTransitionKey}
					position="relative"
					style={getPageTransitionWrapperStyle(playAnimType, playTransitionDuration)}
				>
					<Box
						position="relative"
						width={playViewportDim.width}
						height={playViewportDim.height}
						style={{
							backgroundColor: isPlayRegPage ? (playPage?.registrationDesign?.backgroundColor || "#fff") : (playPage?.backgroundColor || "#fff"),
							boxShadow: "0 8px 40px rgba(0,0,0,0.4)",
							borderRadius: 8,
							overflow: "hidden",
							transform: `scale(${playScale})`,
							transformOrigin: "center center",
						}}
					>
						{!isPlayRegPage && bgImgResolved && (
							<div
								style={{
									position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
									...(staggerBgStyle || {}),
								}}
							>
								<div
									style={{
										position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
										backgroundImage: `url(${bgImgResolved})`,
										backgroundSize: "cover",
										backgroundPosition: "center",
										opacity: playPage?.backgroundImageOpacity != null ? playPage.backgroundImageOpacity / 100 : 1,
										pointerEvents: "none",
									}}
								/>
							</div>
						)}
						{isPlayRegPage ? (
							<Box display="flex" alignItems="center" justifyContent="center" height="100%" flexDirection="column" gap={2} p={4}>
								<AssignmentIcon style={{ fontSize: 48, color: "#7D00FC" }} />
								<Box fontSize={24} fontWeight={700} color="#1a1a1a">Página de Registro</Box>
								<Box fontSize={14} color="#666">(Vista previa del diseño)</Box>
							</Box>
						) : (
							<Box style={{ position: "relative", zIndex: 1, width: "100%", height: "100%" }}>
							{(() => {
							const { allQuizzesOnPageCompleted, activatedSet } = playButtonShowCtxBase;
							const showCtx = {
								...playButtonShowCtxBase,
								hideAfterExpired,
							};
							return playElements.map((el) => {
								let simHidden = false;
								let simDisabled = false;
								if (el.type === "button") {
									simHidden = isButtonHidden(el, showCtx);
									simDisabled = !!el.content?.onlyOnce && activatedSet.has(el.elementId);
								}
								if (el.type === "button" && simHidden) return null;
								const showWhenBtn = el.type === "button" ? (el.content?.showWhen || "always") : "";
								const revealedAfter =
									showWhenBtn === "afterSeconds" && afterSecondsReady.has(String(el.elementId));
								const staggerIdx = pageStagger?.map.get(String(el.elementId));
								const staggerCtx = {
									showWhenBtn,
									revealedAfter,
									buttonVisible: !simHidden,
								};
								let activeStaggerType;
								if (staggerIdx != null) {
									if (isComicPlayTransition && shouldApplyComicPageElementTransition(staggerCtx)) {
										activeStaggerType = "comic";
									} else if (isScatterPlayTransition && shouldApplyScatterPageElementTransition(staggerCtx)) {
										activeStaggerType = "scatter";
									} else if (isRevealPlayTransition && shouldApplyRevealPageElementTransition(staggerCtx)) {
										activeStaggerType = "reveal";
									} else if (
										isStaggerEffectPlayTransition
										&& shouldApplyStaggerEffectPageElementTransition(playAnimType, staggerCtx)
									) {
										activeStaggerType = playAnimType;
									}
								}
								const playEntryAnimationStyle = getElementEntryAnimationStyle(el, playPageAnimKey, {
									pageTransitionType: activeStaggerType,
									staggerIndex: activeStaggerType ? staggerIdx : undefined,
									staggerTotal: pageStagger?.total,
									pageTransitionDurationMs: playTransitionDuration || 550,
								});
								return (
									<ElementRenderer
										key={`${el.elementId}-${playPageAnimKey}`}
										element={el}
										playPageAnimKey={playPageAnimKey}
										playEntryAnimationStyle={playEntryAnimationStyle}
										selected={false}
										onSelect={() => {}}
										onUpdate={() => {}}
										onDragStart={() => {}}
										onResizeStart={() => {}}
										preview={false}
										isLocked
										registrationDesign={playPage?.registrationDesign}
										onButtonAction={handlePlayButtonAction}
										quizData={el.type === "quiz" ? quizzes.find((q) => String(q?._id) === resolvePlayQuizId(el.content?.quizId)) : undefined}
										editorBreakpointId={playViewport}
										simulateQuiz
										playQuizAnswer={playQuizAnswers}
										onQuizOptionSelect={handleQuizOptionSelect}
										onQuizCustomAnswer={handleQuizCustomAnswer}
										onQuizHintUsed={handleQuizHintUsed}
										onHintPlayModal={setPlayHintModal}
										allQuizzesOnPageCompleted={allQuizzesOnPageCompleted}
										simulateButtonHidden={simHidden}
										simulateButtonDisabled={simDisabled}
									/>
								);
							});
						})()}
							</Box>
						)}
						{!isPlayRegPage && playPage?.showFooter && Array.isArray(scenarios) && scenarios.length > 0 && (() => {
							const sorted = scenarios.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
							const currentScId = (playPage?.scenarioId || "").trim();
							const scenarioPassed = (sc) => {
								const idxs = pages.map((p, i) => ((p?.scenarioId || "") === sc.id ? i : -1)).filter((i) => i >= 0);
								return idxs.length > 0 && playPageIndex > Math.max(...idxs);
							};
							const visible = sorted.filter((s) => !playSuppressedScenarioIds.has(s.id));
							const branchGroups = computeExclusiveBranchGroupsFromPlay(pages);
							const visitedScenarios = scenariosVisitedUpToPlayPage(pages, playPageIndex);
							const stations = [];
							let vi = 0;
							while (vi < visible.length) {
								const sc = visible[vi];
								const scN = normScId(sc.id);
								const group = branchGroups.find((g) => g.has(scN));
								const enteredAnyInGroup = group && [...group].some((id) => visitedScenarios.has(id));
								const inVisible = group ? [...group].filter((id) => visible.some((v) => normScId(v.id) === id)) : [];
								if (group && !enteredAnyInGroup && inVisible.length > 1 && group.has(scN)) {
									stations.push({ kind: "branch", ids: inVisible });
									while (vi < visible.length && group.has(normScId(visible[vi].id))) vi++;
									continue;
								}
								stations.push({ kind: "scene", sc });
								vi++;
							}
							const accentColors = ["#FF6017", "#8c3888", "#69D0FF", "#53CE76"];
							return (
								<Box
									style={{
										position: "absolute",
										left: 0,
										right: 0,
										height: 110,
										backgroundColor: "#7D00FC",
										zIndex: 50,
										bottom: playFooterOpen ? 0 : -110,
										transition: "bottom 0.35s ease",
										pointerEvents: "auto",
										boxSizing: "border-box",
									}}
								>
									<Box
										onClick={(e) => { e.stopPropagation(); setPlayFooterOpen((v) => !v); }}
										style={{
											position: "absolute",
											top: -35,
											right: 12,
											width: 52,
											height: 52,
											borderRadius: "50%",
											background: "#7D00FC",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											cursor: "pointer",
											boxShadow: "0 10px 26px rgba(0,0,0,0.25)",
											zIndex: 2,
										}}
										title={playFooterOpen ? "Plegar" : "Desplegar"}
									>
										{playFooterOpen ? <ExpandMoreIcon style={{ color: "#fff", fontSize: 28 }} /> : <ExpandLessIcon style={{ color: "#fff", fontSize: 28 }} />}
									</Box>
									<Box
										display="flex"
										flexWrap="wrap"
										justifyContent="center"
										px={2}
										style={{
											alignItems: "flex-start",
											overflowX: "auto",
											height: "100%",
											paddingTop: 12,
											paddingBottom: 8,
											boxSizing: "border-box",
										}}
									>
										{stations.map((st, idx) => {
											const prev = idx > 0 ? stations[idx - 1] : null;
											const railPassed = prev?.kind === "scene" && scenarioPassed(prev.sc);
											const key = st.kind === "branch" ? `branch-${st.ids.join("-")}` : st.sc.id;
											return (
												<React.Fragment key={key}>
													{idx > 0 && (
														<Box style={{ flex: "0 0 auto", display: "flex", alignItems: "center", height: 46, padding: "0 2px", alignSelf: "flex-start" }} aria-hidden>
															<Box style={{ width: 32, height: 4, borderRadius: 999, background: railPassed ? "rgba(255,255,255,0.82)" : "rgba(255,255,255,0.38)" }} />
														</Box>
													)}
													{st.kind === "branch" ? (
														<Box display="flex" flexDirection="column" alignItems="center" style={{ minWidth: 100, maxWidth: 200, flex: "0 0 auto", gap: 6 }}>
															<Box
																style={{
																	width: 46,
																	height: 46,
																	borderRadius: "50%",
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	background: "rgba(0,0,0,0.22)",
																	border: "2px dashed rgba(255,255,255,0.75)",
																	boxSizing: "border-box",
																}}
															>
																<Box style={{ color: "#fff", fontWeight: 900, fontSize: 22, lineHeight: 1 }}>?</Box>
															</Box>
															<Box textAlign="center" style={{ width: "100%" }}>
																<Box style={{ color: "#fff", fontWeight: 800, fontSize: 13 }}>Por descubrir</Box>
																<Box style={{ color: "rgba(255,255,255,0.75)", fontWeight: 600, fontSize: 11, marginTop: 2 }}>Varios caminos posibles</Box>
															</Box>
														</Box>
													) : (() => {
														const sc = st.sc;
														const sidx = sorted.findIndex((s) => s.id === sc.id);
														const isCurrent = currentScId === sc.id;
														const passed = scenarioPassed(sc);
														const IconComp = sc.icon && BUTTON_ICON_MAP[sc.icon] ? BUTTON_ICON_MAP[sc.icon] : FlagIcon;
														const accent = accentColors[(sidx >= 0 ? sidx : 0) % accentColors.length] || "#7D00FC";
														return (
															<Box display="flex" flexDirection="column" alignItems="center" style={{ minWidth: 100, maxWidth: 200, flex: "0 0 auto", gap: 6 }}>
																<Box
																	style={{
																		width: 46,
																		height: 46,
																		borderRadius: "50%",
																		display: "flex",
																		alignItems: "center",
																		justifyContent: "center",
																		background: isCurrent ? accent : passed ? accent : "rgba(255,255,255,0.18)",
																		border: isCurrent ? "2px solid rgba(255,255,255,0.95)" : passed ? "2px solid rgba(255,255,255,0.85)" : "2px solid rgba(255,255,255,0.55)",
																		boxSizing: "border-box",
																		boxShadow: isCurrent ? "0 10px 22px rgba(0,0,0,0.22)" : "none",
																	}}
																>
																	<IconComp style={{ fontSize: 20, color: "#fff" }} />
																</Box>
																<Box textAlign="center" style={{ width: "100%" }}>
																	<Box style={{ color: "#fff", fontWeight: 800, fontSize: 13, lineHeight: 1.2 }}>{sc.name || `Escenario ${sidx + 1}`}</Box>
																	<Box style={{ color: "rgba(255,255,255,0.75)", fontWeight: 600, fontSize: 11, marginTop: 2 }}>{isCurrent ? "Ahora" : passed ? "Completado" : "Pendiente"}</Box>
																</Box>
															</Box>
														);
													})()}
												</React.Fragment>
											);
										})}
									</Box>
								</Box>
							);
						})()}
						{playAnimType === "glitch" ? (
							<EscapeRoomPageGlitchOverlay
								durationMs={playTransitionDuration || 350}
								overlayKey={`${playTransitionKey}-glitch`}
							/>
						) : null}
						{playAnimType === "mist" ? (
							<EscapeRoomPageMistOverlay
								durationMs={playTransitionDuration || 550}
								overlayKey={`${playTransitionKey}-mist`}
							/>
						) : null}
						{playAnimType === "build" ? (
							<EscapeRoomPageBlueprintOverlay
								layoutItems={(pageStagger?.sorted || []).slice(0, 8).map((el) => ({
									top: el.y,
									left: el.x,
									width: el.width,
									height: el.height,
								}))}
								staggerMs={90}
								overlayKey={`${playTransitionKey}-build`}
							/>
						) : null}
					</Box>
				</Box>
			</Box>
			<Box display="flex" alignItems="center" justifyContent="space-between" px={4} py={2} style={{ background: "rgba(15,23,42,0.95)" }}>
				<Button disabled={playPageIndex <= 0} onClick={handlePlayPrev} style={{ color: "#fff", textTransform: "none" }}>
					← Anterior
				</Button>
				<Box display="flex" alignItems="center" style={{ gap: 16 }}>
					<Box fontSize={14} color="#94a3b8">
						Página {playPageIndex + 1} de {pages.length}
					</Box>
					<Box fontSize={14} fontWeight={600} color="#fbbf24">
						Puntos: {totalPointsDisplay}
					</Box>
				</Box>
				<Button disabled={playPageIndex >= pages.length - 1} onClick={handlePlayNext} style={{ color: "#fff", textTransform: "none" }}>
					Siguiente →
				</Button>
			</Box>
			{playQuizFeedbackModal && (
				<Box
					position="fixed"
					top={0}
					left={0}
					right={0}
					bottom={0}
					zIndex={10003}
					display="flex"
					alignItems="center"
					justifyContent="center"
					style={{ background: "rgba(0,0,0,0.5)", animation: "er-feedback-fadeIn 0.2s ease-out" }}
				>
					<Box
						style={{
							background: "#fff",
							borderRadius: 20,
							boxShadow: "0 24px 80px rgba(0,0,0,0.25)",
							padding: "32px 40px",
							maxWidth: 380,
							textAlign: "center",
							animation: playQuizFeedbackModal.correct ? "er-feedback-popCorrect 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)" : "er-feedback-popWrong 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
						}}
					>
						<Box
							style={{
								width: 80,
								height: 80,
								margin: "0 auto 20px",
								borderRadius: "50%",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								fontSize: 42,
								background: playQuizFeedbackModal.correct ? "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)" : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
								color: "#fff",
								boxShadow: playQuizFeedbackModal.correct ? "0 8px 24px rgba(34,197,94,0.4)" : "0 8px 24px rgba(239,68,68,0.4)",
								animation: "er-feedback-iconBounce 0.6s ease-out 0.2s both",
							}}
						>
							{playQuizFeedbackModal.correct ? "✓" : "✗"}
						</Box>
						<Box fontSize={24} fontWeight={700} color="#1e293b" mb={1}>
							{playQuizFeedbackModal.correct ? "¡Correcto!" : "Incorrecto"}
						</Box>
						<Box fontSize={18} fontWeight={600} color={playQuizFeedbackModal.delta >= 0 ? "#22c55e" : "#ef4444"} mb={playQuizFeedbackModal.totalCorrect > 1 && !playQuizFeedbackModal.done ? 1 : 3}>
							{playQuizFeedbackModal.delta >= 0 ? `+${playQuizFeedbackModal.delta}` : playQuizFeedbackModal.delta} puntos
						</Box>
						{playQuizFeedbackModal.totalCorrect > 1 && !playQuizFeedbackModal.done && (
							<Box mb={3}>
								<Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 10, marginBottom: 10 }}>
									{Array.from({ length: playQuizFeedbackModal.totalCorrect }).map((_, i) => (
										<Box
											key={i}
											style={{
												width: 36,
												height: 36,
												borderRadius: "50%",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												backgroundColor: i < playQuizFeedbackModal.correctCount ? "#22c55e" : "#e2e8f0",
												color: i < playQuizFeedbackModal.correctCount ? "#fff" : "#94a3b8",
												fontSize: 18,
												fontWeight: 700,
												border: i < playQuizFeedbackModal.correctCount ? "none" : "2px solid #cbd5e1",
											}}
										>
											{i < playQuizFeedbackModal.correctCount ? "✓" : ""}
										</Box>
									))}
								</Box>
								<Box fontSize={14} color="#64748b" fontWeight={500} textAlign="center">
									<Box component="span" color="#22c55e" fontWeight={600}>{playQuizFeedbackModal.correctCount}</Box> de {playQuizFeedbackModal.totalCorrect} — faltan <Box component="span" fontWeight={600}>{playQuizFeedbackModal.totalCorrect - playQuizFeedbackModal.correctCount}</Box>
								</Box>
							</Box>
						)}
						{playQuizFeedbackModal.done && (
							<Box mb={3} p={2} style={{ borderRadius: 12, background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)", border: "1px solid #e2e8f0" }}>
								<Box fontSize={13} fontWeight={600} color="#64748b" mb={1}>Resultado del quiz</Box>
								<Box display="flex" alignItems="center" justifyContent="center" flexWrap="wrap" style={{ gap: 16 }}>
									<Box component="span" fontSize={15} color="#334155">
										<Box component="span" color="#22c55e" fontWeight={700}>{playQuizFeedbackModal.correctCount}</Box> acertadas
									</Box>
									{playQuizFeedbackModal.incorrectCount > 0 && (
										<Box component="span" fontSize={15} color="#334155">
											<Box component="span" color="#ef4444" fontWeight={700}>{playQuizFeedbackModal.incorrectCount}</Box> falladas
										</Box>
									)}
									<Box component="span" fontSize={18} fontWeight={700} color={playQuizFeedbackModal.totalPoints >= 0 ? "#22c55e" : "#ef4444"}>
										Total: {playQuizFeedbackModal.totalPoints >= 0 ? "+" : ""}{playQuizFeedbackModal.totalPoints} pts
									</Box>
								</Box>
							</Box>
						)}
						{playQuizFeedbackModal.explanation && (
							<Box
								mb={3}
								p={2.5}
								style={{
									borderRadius: 16,
									background: "linear-gradient(145deg, #eff6ff 0%, #dbeafe 100%)",
									border: "1px solid #93c5fd",
									boxShadow: "0 2px 8px rgba(59, 130, 246, 0.08)",
									textAlign: "left",
								}}
							>
								<Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 12 }}>
									<Box
										style={{
											width: 32,
											height: 32,
											borderRadius: 10,
											background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											fontSize: 18,
											color: "#fff",
										}}
									>
										💡
									</Box>
									<Box fontSize={14} fontWeight={700} color="#1e40af" letterSpacing="0.02em">Explicación</Box>
								</Box>
								<Box fontSize={15} color="#1e3a8a" lineHeight={1.6} style={{ whiteSpace: "pre-wrap" }}>{playQuizFeedbackModal.explanation}</Box>
							</Box>
						)}
						<Button
							variant="contained"
							onClick={() => setPlayQuizFeedbackModal(null)}
							style={{
								padding: "12px 32px",
								fontSize: 16,
								fontWeight: 600,
								background: playQuizFeedbackModal.correct ? "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)" : "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
								color: "#fff",
								borderRadius: 12,
								boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
								textTransform: "none",
							}}
						>
							Continuar
						</Button>
					</Box>
				</Box>
			)}
			{playHintModal?.type === "confirm" && (
				<Dialog
					open
					onClose={() => setPlayHintModal(null)}
					ModalProps={{ style: { zIndex: 10001 }, BackdropProps: { style: { zIndex: 10001, animation: "er-modal-overlay-in 0.22s ease-out both" } } }}
					PaperProps={{ style: { zIndex: 10001, animation: "er-modal-content-in 0.28s cubic-bezier(0.34, 1.2, 0.64, 1) both" } }}
				>
					<DialogTitle>Pista</DialogTitle>
					<DialogContent>Vas a perder {playHintModal.penalty} puntos. ¿Estás seguro?</DialogContent>
					<DialogActions>
						<Button onClick={() => setPlayHintModal(null)}>Cancelar</Button>
						<Button
							onClick={() => {
								setPlayHintModal({ type: "hint", text: playHintModal.text });
								handleQuizHintUsed(playHintModal.elementId, playHintModal.penalty);
							}}
							variant="contained"
							color="primary"
						>
							Aceptar
						</Button>
					</DialogActions>
				</Dialog>
			)}
			{playHintModal?.type === "hint" && (
				<Dialog
					open
					onClose={() => setPlayHintModal(null)}
					ModalProps={{ style: { zIndex: 10001 }, BackdropProps: { style: { zIndex: 10001, animation: "er-modal-overlay-in 0.22s ease-out both" } } }}
					PaperProps={{ style: { zIndex: 10001, animation: "er-modal-content-in 0.28s cubic-bezier(0.34, 1.2, 0.64, 1) both" } }}
				>
					<DialogTitle>Pista</DialogTitle>
					<DialogContent><div style={{ whiteSpace: "pre-wrap" }}>{playHintModal.text}</div></DialogContent>
					<DialogActions>
						<Button onClick={() => setPlayHintModal(null)} variant="contained" color="primary">Cerrar</Button>
					</DialogActions>
				</Dialog>
			)}
			{playModalContent && (
				<Box
					position="fixed"
					top={0}
					left={0}
					right={0}
					bottom={0}
					zIndex={10002}
					display="flex"
					alignItems="center"
					justifyContent="center"
					onClick={(e) => e.target === e.currentTarget && setPlayModalContent(null)}
					style={{ background: "rgba(0,0,0,0.6)", animation: "er-modal-overlay-in 0.22s ease-out both" }}
				>
					<Box
						position="relative"
						width={playModalContent.width}
						height={playModalContent.height}
						style={{
							backgroundColor: playModalContent.design?.backgroundColor || "#ffffff",
							borderRadius: playModalContent.design?.borderRadius ?? 12,
							boxShadow: "0 12px 48px rgba(0,0,0,0.4)",
							overflow: "hidden",
							animation: "er-modal-content-in 0.28s cubic-bezier(0.34, 1.2, 0.64, 1) both",
						}}
						onClick={(e) => e.stopPropagation()}
					>
						{(() => {
							const { allQuizzesOnPageCompleted, activatedSet } = modalButtonShowCtxBase;
							const showCtx = {
								...modalButtonShowCtxBase,
								hideAfterExpired: modalHideAfterExpired,
							};
							return (playModalContent.design?.elements || []).map((el) => {
								let simHidden = false;
								let simDisabled = false;
								if (el.type === "button") {
									simHidden = isButtonHidden(el, showCtx);
									simDisabled = !!el.content?.onlyOnce && activatedSet.has(el.elementId);
								}
								if (el.type === "button" && simHidden) return null;
								return (
							<ElementRenderer
								key={el.elementId}
								element={el}
								selected={false}
								onSelect={() => {}}
								onUpdate={() => {}}
								onDragStart={() => {}}
								onResizeStart={() => {}}
								preview={false}
								isLocked
								onButtonAction={handlePlayButtonAction}
								quizData={el.type === "quiz" ? quizzes.find((q) => String(q?._id) === resolvePlayQuizId(el.content?.quizId)) : undefined}
								editorBreakpointId={playViewport}
								simulateQuiz
								playQuizAnswer={playQuizAnswers}
								onQuizOptionSelect={handleQuizOptionSelect}
								onQuizCustomAnswer={handleQuizCustomAnswer}
								onQuizHintUsed={handleQuizHintUsed}
								allQuizzesOnPageCompleted={allQuizzesOnPageCompleted}
								simulateButtonHidden={simHidden}
								simulateButtonDisabled={simDisabled}
								playerScore={totalPointsDisplay}
							/>
								);
							});
						})()}
						<IconButton
							onClick={() => setPlayModalContent(null)}
							style={{ position: "absolute", top: 8, right: 8, background: "rgba(255,255,255,0.9)", zIndex: 10 }}
							size="small"
							title="Cerrar"
						>
							<CloseIcon />
						</IconButton>
					</Box>
				</Box>
			)}
		</Box>
	);
}

export { PlayModeView };
